import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import GenericTable from "../../../components/Commons/Table/generic-table.component";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import ShowDataLength from "../../../components/Commons/Table/results-length-table.component";
import { useEffect, useState } from "react";
import {
  getAll,
  remove,
  update,
  importCustomers,
} from "../../../services/customers.service";
import { PaginationProps } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import { useNavigate } from "react-router-dom";
import { AlertService } from "../../../services/alert.service";
import { useMutation, useQuery } from "react-query";
import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";
import ExportBtn from "../../../components/Commons/Export/ExportButton";
import { downloadExcel } from "../../../services/utils";
import ProfilePermissionService from "../../../services/profilePermissions.service";
import { isMobile } from "react-device-detect";
import FilterButton from "../../../components/Commons/Table/FilterButton";
import ImportBtn from "../../../components/Commons/Import/ImportButton";

const { Search } = Input;

export default function List(props: any) {
  const [customers, setCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState<number | undefined>(1);
  const [perPage, setPerPage] = useState<number | undefined>(10);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<string>("name-asc");
  const [status, setStatus] = useState<string>("");
  const [removeCustomer, setRemoveCustomer] = useState<any | undefined>(
    undefined
  );
  const [customersToExport, setCustomersToExport] = useState([]);
  const [exportIsLoading, setExportIsLoading] = useState<boolean>(false);
  const [formSearch] = Form.useForm();
  const [formFilter] = Form.useForm();

  const navigate = useNavigate();

  const profilePermissionService = ProfilePermissionService.getInstance();

  const { isLoading: customerLoading, refetch: loadCustomers } = useQuery(
    ["loadCustomers", currentPage, search, order, perPage, status],
    () =>
      getAll({
        page: currentPage,
        per_page: perPage,
        order,
        search,
        status,
      }),
    {
      onSuccess: (response) => {
        setCustomers(
          response.data.map((customer: any) => {
            return {
              key: customer.data.id,
              ...customer.data,
              ...customer.relationships,
            };
          })
        );

        setCustomersToExport([]);
        setCurrentPage(response.meta.current_page);
        setPerPage(response.meta.per_page);
        setTotal(response.meta.total);
      },
      refetchOnWindowFocus: false,
    }
  );

  const { refetch: loadCustomersToExport } = useQuery(
    ["loadCustomersToExport"],
    () => getAll({ order, search, status }),
    {
      onSuccess: (response) =>
        setCustomersToExport(props.onMapDataToExport(response.data)),
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  const { mutate: removeCustomerMutate } = useMutation(remove, {
    onSuccess: () => {
      AlertService.sendAlert([{ text: `${props.label} apagado com sucesso.` }]);
      setRemoveCustomer(undefined);
      loadCustomers();
    },
  });

  const onSearch = (search_value: any) => {
    setCurrentPage(1);
    setSearch(search_value);
  };

  const onFilter = () => {
    setCurrentPage(1);
    setStatus(formFilter.getFieldValue("filter_status"));
  };

  const onClearFilters = () => {
    setCurrentPage(1);
    setStatus("");
  };

  const handleTableChange = (
    pagination: PaginationProps,
    filters: Record<string, FilterValue | null>,
    sorter: any
  ) => {
    if (pagination.current !== currentPage) {
      setCurrentPage(pagination.current);
    }

    if (pagination.pageSize !== perPage) {
      setPerPage(pagination.pageSize);
      setCurrentPage(1);
    }

    if (sorter.order) {
      let orderBy = `${sorter.field}-${sorter.order.substr(
        0,
        sorter.order.length - 3
      )}`;

      if (order !== orderBy) {
        setOrder(orderBy);
      }
    }
  };

  const toPage = (page: string, customer_id: number = 0) => {
    let pages = {
      create: "/management/customers/create",
      edit: `/management/customers/${customer_id}/edit`,
    };

    navigate(pages[page as keyof typeof pages]);
  };

  const exportData = () => downloadExcel(customersToExport, "Clientes");

  const { mutate: updateActiveStatusMutate } = useMutation(update, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Status do cliente atualizado com sucesso." },
      ]);
    },
  });

  const onChangeActiveStatus = (checked: boolean, id: any) => {
    const obj = {
      values: { active: checked },
      id,
    };

    updateActiveStatusMutate(obj);
  };

  const columns: any = [
    ...props.columns,
    {
      title: "Ativo?",
      dataIndex: "active",
      key: "active",
      sorter: true,
      width: 100,
      render: (_: any, record: any) => {
        return (
          <Tooltip
            title={record.active ? "Desativar cliente" : "Ativar cliente"}
          >
            <Switch
              checked={record.active}
              onChange={(event: boolean) => {
                onChangeActiveStatus(event, record.uuid);
                record.active = event;
              }}
            />
          </Tooltip>
        );
      },
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ações",
      key: "action",
      width: 60,
      render: (_: any, record: any) => (
        <Space size="small" direction={isMobile ? "vertical" : "horizontal"}>
          <Tooltip placement="top" title="Detalhes">
            <Button
              type="default"
              shape="circle"
              size="middle"
              icon={<EyeOutlined />}
              onClick={() => props.onOpenDetails(record)}
            />
          </Tooltip>

          {profilePermissionService.hasPermission(
            "customers-customers:write"
          ) && (
            <>
              <Tooltip placement="top" title="Editar">
                <Button
                  type="default"
                  shape="circle"
                  size="middle"
                  icon={<EditOutlined />}
                  onClick={() => toPage("edit", record.uuid)}
                />
              </Tooltip>

              <Tooltip placement="top" title="Apagar">
                <Button
                  type="default"
                  shape="circle"
                  size="middle"
                  icon={<DeleteOutlined />}
                  onClick={() => setRemoveCustomer(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const filters = (
    <Form
      form={formFilter}
      name="cilter_customers"
      layout="vertical"
      className="form_filter_horizontal"
      autoComplete="off"
      style={isMobile ? {} : { width: 300 }}
    >
      <Row gutter={20} style={{ width: "100%" }}>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Form.Item
            label="Estado"
            name="filter_status"
            className="item_filter"
          >
            <Select
              placeholder="Todos"
              value={status}
              style={{ width: "100%" }}
              size="large"
              options={[
                { value: "", label: "Todos" },
                { value: "active", label: "Ativos" },
                { value: "inactive", label: "Não Ativos" },
              ]}
            />
          </Form.Item>
        </Col>

        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Button onClick={onFilter} size="large">
            Filtrar
          </Button>

          {status && (
            <Button type="link" onClick={onClearFilters} size="large">
              Limpar
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  );

  useEffect(() => {
    formFilter.setFieldsValue({ filter_status: "" });
  }, []);

  useEffect(() => {
    formFilter.setFieldsValue({ filter_status: status });
  }, [status]);

  useEffect(() => {
    if (customersToExport.length) {
      exportData();
    }

    setExportIsLoading(false);
  }, [customersToExport]);

  return (
    <>
      <Card
        styles={{
          body: {
            padding: 8,
          },
        }}
      >
        <Row>
          <Col
            lg={{ span: 12 }}
            md={{ span: 12 }}
            sm={{ span: 24 }}
            xs={{ span: 24 }}
          >
            <Form form={formSearch} name="cearch_customers" autoComplete="off">
              <Form.Item name="cearch_customer">
                <Search
                  onSearch={onSearch}
                  placeholder={`Pesquise por alguma informação do cliente`}
                  enterButton="Pesquisar"
                  allowClear
                  size="large"
                  maxLength={255}
                />
              </Form.Item>
            </Form>
          </Col>

          {profilePermissionService.hasPermission(
            "customers-customers:write"
          ) && (
            <Col
              lg={{ span: 4, offset: 8 }}
              md={{ span: 4, offset: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Button
                type="primary"
                onClick={() => toPage("create")}
                size="large"
                block
              >
                Criar novo
              </Button>
            </Col>
          )}
        </Row>

        <div className="table">
          <div style={{ marginBottom: 16 }}>
            {status && (
              <Tag
                closable
                onClose={() => {
                  setStatus("");
                  formFilter.setFieldValue("filter_status", "");
                  setCurrentPage(1);
                }}
              >
                Estado: {status === "active" ? "Ativos" : "Não Ativos"}
              </Tag>
            )}
          </div>
          <GenericTable
            columns={columns}
            dataSource={customers}
            pagination={{
              current: currentPage,
              pageSize: perPage,
              showSizeChanger: true,
              total: total,
            }}
            loading={customerLoading}
            totalChildren={
              <div className="" style={isMobile ? {} : { marginTop: 8 }}>
                <Row>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                    style={isMobile ? { marginBottom: 10, marginTop: 10 } : {}}
                  >
                    <ShowDataLength size={total} className="show_result_size" />
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Space>
                      <FilterButton filters={filters} />

                      <ImportBtn
                        uploadFunction={importCustomers}
                        block={isMobile}
                        multiple={false}
                        onSuccess={() => {
                          AlertService.sendAlert([
                            { text: "Importação concluída com sucesso." },
                          ]);

                          loadCustomers();
                        }}
                      />

                      <ExportBtn
                        disabled={!total}
                        loading={exportIsLoading}
                        block={isMobile}
                        onClick={() => {
                          if (customersToExport.length) {
                            exportData();
                          } else {
                            setExportIsLoading(true);
                            loadCustomersToExport();
                          }
                        }}
                      />
                    </Space>
                  </Col>
                </Row>
              </div>
            }
            empty={{
              createTheFirst: !customers.length && !search && !status,
              textOne:
                "Esta tabela encontra-se vazia, uma vez que ainda não foram criados clientes.",
              textTwo: profilePermissionService.hasPermission(
                "customers-customers:write"
              )
                ? "Para começar, crie um novo cliente."
                : "",
              link: profilePermissionService.hasPermission(
                "customers-customers:write"
              )
                ? "/management/customers/create"
                : "",
            }}
            handleTableChange={handleTableChange}
          />

          <ConfirmModal
            open={!!removeCustomer}
            title={`Tem certeza que deseja apagar o cliente "${removeCustomer?.name}"?`}
            okText="Apagar"
            cancelText="Voltar"
            onOk={() => removeCustomerMutate(removeCustomer?.uuid)}
            onCancel={() => setRemoveCustomer(undefined)}
          ></ConfirmModal>
        </div>
      </Card>
    </>
  );
}
